import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { TotalAndFeesParams } from './types'

import { RowItem, Separator, TransactionFeeDescription } from '@/components'
import { BaseView } from '@/components/base'
import { FiatCode } from '@/constants/currencyCodes'
import { useCalcOnRampCryptoPriceQuotes } from '@/domain/transfer/useCases/useCalcOnRampCryptoPriceQuotes'
import { transferAdapters } from '@/domain/transfer/transferAdapters'
import { useSendStore } from '@/store/useSendStore'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'

const { adapterCryptoPriceQuotesFees } = transferAdapters

export const TotalAndFees: React.FC<TotalAndFeesParams> = ({
  recipientParams,
  paymentMethod,
}) => {
  const { currencyValue, currencyCode, type = 'send' } = recipientParams
  const { t } = useTranslation()

  const paymentCurrencyCode = useAppPreferencesStore(
    (state) => state.paymentCurrency?.isin,
  )
  const setSendRequestForm = useSendStore((state) => state.setSendRequestForm)

  const isSend = type === 'send'
  const isWallet = paymentMethod === 'Wallet'

  const {
    data: onRampCryptoPriceQuotes,
    isLoading: isLoadingFee,
    isError: isFeeError,
  } = useCalcOnRampCryptoPriceQuotes(
    {
      cryptoCurrency: currencyCode,
      fiatCurrency: paymentCurrencyCode as FiatCode,
      amount: Number(currencyValue),
    },
    {
      enabled: !isWallet && isSend && !!paymentMethod,
    },
  )

  // Filter price quotes by current payment method
  const filteredPriceQuotes = useMemo(() => {
    if (!onRampCryptoPriceQuotes || !paymentMethod) {
      return onRampCryptoPriceQuotes
    }

    return adapterCryptoPriceQuotesFees({
      onRampCryptoPriceQuotes,
      adapterParams: { paymentMethod },
      payload: {
        cryptoCurrency: currencyCode,
        fiatCurrency: paymentCurrencyCode || 'USD',
        amount: Number(currencyValue),
      },
    })
  }, [
    onRampCryptoPriceQuotes,
    paymentMethod,
    currencyCode,
    paymentCurrencyCode,
    currencyValue,
  ])

  useEffect(() => {
    if (filteredPriceQuotes) {
      setSendRequestForm({
        onRampTotalAmount: {
          amount:
            filteredPriceQuotes.priceQuotesByPaymentMethod?.fiat_amount
              .amount || '0',
          code:
            filteredPriceQuotes.priceQuotesByPaymentMethod?.fiat_amount
              .currency || 'USD',
        },
      })
    }
    if (isFeeError) {
      console.error('Error loading transaction fees')
    }
  }, [filteredPriceQuotes, setSendRequestForm, isFeeError])

  return (
    <BaseView gap={24}>
      <Separator />

      <RowItem
        label={t('translation:amount', 'Amount')}
        value={
          filteredPriceQuotes?.formattedAmountWithPaymentCurrencyRelative || ''
        }
        skeletonActive={isLoadingFee}
      />

      <RowItem
        labelProps={{
          color: 'label.disabled',
        }}
        label={t('translation:transactionFee', 'Transaction Fee')}
        valueProps={{
          color: 'label.disabled',
        }}
        value={filteredPriceQuotes?.formattedFiatFee || ''}
        skeletonActive={isLoadingFee}
        IconComponent={<TransactionFeeDescription />}
      />

      <Separator />

      <RowItem
        labelProps={{
          fontSize: 20,
          fontFamily: 'semibold',
          color: 'label.default',
        }}
        label={t('translation:total', 'Total')}
        valueProps={{
          fontSize: 20,
          fontFamily: 'semibold',
        }}
        value={filteredPriceQuotes?.formattedFiatTotal || ''}
        skeletonActive={isLoadingFee}
      />
    </BaseView>
  )
}

export default TotalAndFees
